import { DateHelper } from "@swivl-mono/helpers";

import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { UnitInterface } from "../Unit/Unit.Interface";
import { UserInterface } from "../User/User.Interface";
import { LeasePropKey } from "../Variables/Generated/LeasePropKey.Data";

export interface LeaseInterface extends OrgBaseInterface {
  locationId: number;
  unitId?: number | null;
  userId?: number | null;
  user?: UserInterface;
  tenantId?: number | null;
  active: boolean;
  externalUnitId?: string;
  externalLeaseId?: string;
  externalTenantId?: string;
  fmsType: string;
  moveInDate?: Date;
  moveOutDate?: Date;
  startDate?: Date;
  endDate?: Date;
  rent?: number;
  paidThroughDate?: Date;
  balance?: number;
  /** When `balance` was last read live from the FMS (tenant-page JIT refresh); success only; null until then. */
  balanceFetchedAt?: Date | null;
  /** When the single-lease sync job last tried a live read, success or failure (ENG-3545 D28); null until the first try. */
  balanceFetchAttemptedAt?: Date | null;
  /** How that last try ended; null until the first try. See `LeaseBalanceFetchStatus`. */
  balanceFetchStatus?: LeaseBalanceFetchStatus | null;
  autopay?: boolean;
  daysPastDue?: number;
  accountStatus?: LeaseAccountStatus;
  paymentStatus?: LeaseAccountPaymentStatus;
  dueDate?: Date;
  accessCode?: string;
  accessCodeQueryId?: string;
  /** FMS-side facility identifier this lease was synced under — the source of
   *  truth for scoping per-lease FMS calls like access-code lookups. Populated
   *  by a single bulk UPDATE per location at sync completion (NOT via the
   *  per-lease dirty-merge — that would lock the lease table on first sync). */
  externalFacilityId?: string | null;
  /** ENG-3168: normalized delinquency stage as the FMS reports it (never inferred from
   *  daysPastDue); null = no stage. Precedence when several signals are present:
   *  auction > lien > overlock > gate_lock. */
  delinquencyStatus: LeaseDelinquencyStatus | null;
  /** ENG-3168: the tenant still holds the unit — false only once the rental has ended (moved
   *  out, transferred, closed ledger). Unlike `active`, a delinquency stage (overlock, auction)
   *  never clears it: for SSMCloud and SiteLink `active` also encodes "can pay online", which
   *  the payment surfaces rely on, so sequences use this flag for membership instead. DB
   *  default is false; the first sync after deploy writes it for every lease. */
  tenancyActive: boolean;
  raw?: any;
  unit?: UnitInterface;
}

export const LeaseSyncOnFields = new Set<keyof LeaseInterface>([
  "active",
  "externalUnitId",
  "externalLeaseId",
  "externalTenantId",
  "moveInDate",
  "moveOutDate",
  "startDate",
  "endDate",
  "rent",
  "paidThroughDate",
  "balance",
  "autopay",
  "daysPastDue",
  "accountStatus",
  "paymentStatus",
  "dueDate",
  "accessCode",
  "accessCodeQueryId",
  "userId",
  "tenantId",
  "unitId",
  "delinquencyStatus",
  "tenancyActive"
]);

// This data type is used to normalize the lease data for FMS integration before it is saved or synced.
export type NormalizedFMSLeaseInterface = Omit<
  LeaseInterface,
  "id" | "createdAt" | "updatedAt" | "deletedDate" | "userId" | "user" | "orgId" | "org" | "locationId" | "unit"
>;
export type SaveLeaseInterface = Omit<LeaseInterface, "id" | "deletedDate">;

/**
 * Money as text: always two decimals ("145.50", "80.00"), never "145.5". The copy that uses
 * `{{Lease:Balance}}` / `{{Lease:Rent}}` supplies its own "$", and a voice reads "$145.50" as
 * dollars and cents where "$145.5" came out as "one forty five point five" (live fire 2026-09-22).
 * Numeric columns can arrive as strings, so the value is coerced first.
 */
function moneyText(value: number | string | null | undefined): string {
  if (value == null || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "";
}

export function LeaseVarsForLease(lease: LeaseInterface): { [key in LeasePropKey]: string } {
  // Psuedos Days past due
  // Status
  return {
    [LeasePropKey.Active]: lease.active ? "1" : "0",

    [LeasePropKey.UnitId]: lease.unitId ? lease.unitId.toString() : "",
    [LeasePropKey.UserId]: lease.userId ? lease.userId.toString() : "",
    // [LeasePropKey.AccessCode]: lease.accessCode ? lease.accessCode : '',
    // [LeasePropKey.DivinciCode]: lease.divinciCode ? lease.divinciCode : '',

    [LeasePropKey.TenantId]: lease.tenantId ? lease.tenantId.toString() : "",
    [LeasePropKey.ExternalUnitId]: lease.externalUnitId ? lease.externalUnitId : "",
    [LeasePropKey.ExternalLeaseId]: lease.externalLeaseId ? lease.externalLeaseId : "",
    [LeasePropKey.FMSType]: lease.fmsType,
    [LeasePropKey.MoveInDate]: lease.moveInDate ? DateHelper.normalize(lease.moveInDate).toISOString() : "",
    [LeasePropKey.MoveOutDate]: lease.moveOutDate ? DateHelper.normalize(lease.moveOutDate).toISOString() : "",
    [LeasePropKey.StartDate]: lease.startDate ? DateHelper.normalize(lease.startDate).toISOString() : "",
    [LeasePropKey.EndDate]: lease.endDate ? DateHelper.normalize(lease.endDate).toISOString() : "",
    [LeasePropKey.Rent]: moneyText(lease.rent),
    [LeasePropKey.PaidThroughDate]: lease.paidThroughDate ? DateHelper.normalize(lease.paidThroughDate).toISOString() : "",
    [LeasePropKey.Balance]: moneyText(lease.balance),
    [LeasePropKey.AccountStatus]: lease.accountStatus ? lease.accountStatus : "",
    [LeasePropKey.PaymentStatus]: lease.paymentStatus ? lease.paymentStatus : "",
    [LeasePropKey.AutoPayEnabled]: lease.autopay ? "1" : "0",
    [LeasePropKey.DaysPastDue]: lease.daysPastDue != null ? lease.daysPastDue.toString() : "",
    [LeasePropKey.DueDate]: lease.dueDate ? DateHelper.normalize(lease.dueDate).toISOString() : ""
  };
}

export enum LeaseAccountStatus {
  ACTIVE = "active",
  INACTIVE = "inactive"
}

/** ENG-3168: normalized delinquency stage vocabulary (varchar column + TS enum, no Postgres enum). */
export enum LeaseDelinquencyStatus {
  GATE_LOCK = "gate_lock",
  OVERLOCK = "overlock",
  LIEN = "lien",
  AUCTION = "auction"
}

//NOTE: We may want to add a status for "paid".
/**
 * Outcome of the last live FMS read of one lease (`lease.balanceFetchAttemptedAt`, ENG-3545 D28).
 * - `ok`: a balance came back; `balanceFetchedAt` moved with it.
 * - `rejected`: the FMS answered that it does not have, or will not give, this lease — a 4xx for
 *   the id, or an empty result. Retrying soon will not help; the lease has likely left the FMS.
 * - `unavailable`: the FMS said nothing about the lease — outage, timeout, network error. The next
 *   visit after the back-off retries.
 */
export type LeaseBalanceFetchStatus = "ok" | "rejected" | "unavailable";

export enum LeaseAccountPaymentStatus {
  PAST_DUE = "past due",
  DUE = "due",
  UNKNOWN = "unknown"
}
